import { useState } from "react";
import { Img, Lines, Reveal, SectionHead, WA_URL } from "../lib/site";

const noHace = [
  {
    t: "No sustituye a tu equipo",
    d: "Les quita el teléfono de encima para que estén en la cocina y en la barra.",
  },
  {
    t: "No inventa platos",
    d: "Solo existe lo que está en tu carta. Lo que no tienes, lo dice y ofrece lo más parecido.",
  },
  {
    t: "No cierra a ciegas",
    d: "Repite el pedido entero antes de colgar. Y si no entiende algo, pregunta en vez de suponer.",
  },
  {
    t: "No se hace pasar por una persona",
    d: "Si el cliente pregunta, dice que es el asistente automático del restaurante.",
  },
];

export function LoQueNoHace() {
  return (
    <section className="bg-papel py-28 text-noche md:py-36" data-testid="section-no-hace">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 md:px-8 lg:grid-cols-2">
        <div>
          <SectionHead num="04" label="Lo que no hace" />
          <Lines
            className="mt-10 font-display display-track text-3xl leading-[1.05] sm:text-4xl"
            lines={["Lo que Kanvia", <span key="x" className="serif-accent">no hace.</span>]}
          />
          <Reveal delay={160}>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-noche/70">
              Preferimos decirlo antes de que lo preguntes en la demo.
            </p>
          </Reveal>
          <Reveal delay={220} className="zoomwrap mt-12 aspect-[4/3]">
            <Img
              src="https://images.unsplash.com/photo-1537047902294-62a40c20a6ae?auto=format&fit=crop&q=80&w=1400"
              alt="Sala de restaurante con las mesas puestas antes del servicio, en blanco y negro"
              name="sala-antes-del-servicio"
              className="foto-bw h-full w-full object-cover"
            />
          </Reveal>
        </div>
        <div className="flex flex-col justify-center">
          {noHace.map((b, i) => (
            <Reveal key={b.t} delay={i * 90} className="border-t border-noche/15 py-8">
              <div className="flex items-start gap-5">
                <span className="mt-3 h-px w-8 shrink-0 bg-noche/60" aria-hidden="true" />
                <div>
                  <h3 className="font-display text-xl display-track">{b.t}</h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-noche/75">{b.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Cita() {
  return (
    <section className="relative flex min-h-[80vh] items-center overflow-hidden text-crema" data-testid="section-cita">
      <div className="plx-wrap" data-plx="0.12">
        <div className="plx-in">
          <Img
            src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&q=80&w=2000"
            alt="Interior de restaurante con madera y luz cálida, casi a oscuras"
            name="sala-calida"
            className="h-full w-full object-cover"
          />
        </div>
      </div>
      <div className="absolute inset-0 bg-[#061713]/[0.82]" aria-hidden="true" />
      <div className="relative z-10 mx-auto max-w-5xl px-5 py-32 md:px-8">
        <Lines
          as="p"
          className="font-display display-track text-3xl leading-[1.15] sm:text-4xl lg:text-5xl"
          lines={[
            "«No pierdes clientes por el precio.",
            <span key="x" className="serif-accent">Los pierdes porque nadie coge el teléfono.»</span>,
          ]}
        />
      </div>
    </section>
  );
}

const faqs = [
  {
    q: "¿Y si se equivoca con un pedido?",
    a: "Repite el pedido entero antes de colgar, así que el cliente lo corrige en el momento. Y todas las llamadas quedan grabadas: si algo sale mal, se escucha y se ve exactamente qué pasó. Con un cuaderno y un boli eso no lo tienes.",
  },
  {
    q: "¿Tengo que cambiar de TPV o instalar algo?",
    a: "No. El pedido te llega por correo, por WhatsApp o impreso en la cocina, lo que prefieras. No tocamos tu sistema ni instalamos nada en el local.",
  },
  {
    q: "¿Cuánto se tarda en tenerlo funcionando?",
    a: "Una tarde. Cargamos tu carta, tus horarios y tu zona de reparto, hacemos unas llamadas de prueba juntos y ya está atendiendo. El único trámite por tu parte es pedirle el desvío a tu operador.",
  },
  {
    q: "¿Y si el cliente quiere hablar con una persona?",
    a: "Le pasa la llamada. Y también lo hace por su cuenta si detecta una alergia grave o algo que no está claro en la carta. No insiste ni le da largas.",
  },
  {
    q: "¿Entiende a la gente que habla rápido o con acento?",
    a: "Sí, y conoce los nombres de tu carta: dürüm, campero, ayran, lo que sea. Si algo no lo entiende, pide que se lo repitan como haría cualquiera. Lo que no hace nunca es suponer.",
  },
  {
    q: "¿Y si un mes tengo más llamadas de las contratadas?",
    a: "Te avisamos antes de llegar al límite y lo ajustamos. Nunca se corta el servicio de golpe ni aparece un cargo que no esperabas.",
  },
];

export function Preguntas() {
  const [open, setOpen] = useState(0);
  return (
    <section id="preguntas" className="bg-noche py-28 text-crema md:py-36" data-testid="section-preguntas">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 md:px-8 lg:grid-cols-[2fr_3fr]">
        <Reveal className="zoomwrap aspect-[4/5] self-start">
          <Img
            src="https://images.unsplash.com/photo-1572116469696-31de0f17cc34?auto=format&fit=crop&q=80&w=1000"
            alt="Barra de un restaurante iluminada con luz cálida"
            name="barra-en-servicio"
          />
        </Reveal>
        <div>
          <SectionHead num="05" label="Lo que nos preguntáis" />
          <Lines
            className="mt-10 font-display display-track text-3xl leading-[1.05] sm:text-4xl"
            lines={["Las dudas de siempre,", <span key="x" className="serif-accent">contestadas.</span>]}
          />
          <div className="mt-12">
            {faqs.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={f.q} className="border-t border-salvia/20 last:border-b">
                  <button
                    type="button"
                    className="acc-btn flex min-h-[48px] w-full items-center justify-between gap-6 py-6 text-left"
                    aria-expanded={isOpen}
                    aria-controls={`faq-panel-${i}`}
                    data-testid={`faq-button-${i}`}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                  >
                    <span className="font-display text-lg display-track">{f.q}</span>
                    <span className="acc-plus text-xl text-salvia" aria-hidden="true">+</span>
                  </button>
                  <div id={`faq-panel-${i}`} className={`acc-panel${isOpen ? " open" : ""}`} role="region">
                    <div className="acc-inner">
                      <p className="max-w-2xl pb-7 text-sm leading-relaxed text-crema/80">{f.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <Reveal delay={120}>
            <p className="mt-10 font-mono text-[12px] leading-relaxed opacity-80">
              ¿Tienes otra duda?{" "}
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-4 transition-opacity duration-300 hover:opacity-70"
                data-testid="faq-whatsapp-link"
              >
                Escríbenos por WhatsApp
              </a>{" "}
              y te la contestamos hoy.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
